import React, { FunctionComponent, useRef, useState } from 'react'
import { Pressable } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { DropdownMenu, MENU_SELECTED_ITEM_STYLE } from '../../atoms/formElements/typeAhead/DropdownMenu'
import IconButton from '../../atoms/iconButton/IconButton'
import { DefaultAvatar } from '../../atoms/icons/Avatar'
import { LyraLogo } from '../../atoms/icons/LyraLogo'
import { SideNavHelpIcon } from '../../atoms/icons/SideNavHelpIcon'
import { SideNavSearchIcon } from '../../atoms/icons/SideNavSearchIcon'
import { SideNavSettingsIcon } from '../../atoms/icons/SideNavSettingsIcon'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { Menu, MenuItemProps } from '../menu/Menu'
import { TabDropdownProps } from '../tabDropdown/TabDropdown'

// Define the props for our SideNavigationBar
export interface SideNavigationBarProps {
  handleLogoClick?: () => void
  navigationItems?: TabDropdownProps[]
  title?: string
  menuItemsList: MenuItemProps[]
  onSupportPress?: () => void
  hasInAppCalendarSettings: boolean
  onSearchPress: () => void
  onSettingPress: () => void
  videoSessionOpen: boolean
  appLocked?: boolean
  videoSessionBannerHeight: number
  hideSearchNav?: boolean
  hideSettingsNav?: boolean
  showSettingsNotificationBubble?: boolean
  /** Called with an item's `activePathname` when the item has no `onPress` of its own */
  onNavigate: (path: string) => void
  /** Whether the app's settings page is the current page */
  isSettingsActive: boolean
}

// Main container for the side navigation
const Container = styled.View<{ theme: ThemeType; videoSessionOpen: boolean; videoSessionBannerHeight: number }>(
  ({ theme, videoSessionOpen, videoSessionBannerHeight }) => ({
    display: 'flex',
    width: '102px',
    padding: `${theme.spacing['24px']} ${theme.spacing['16px']}`,
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    backgroundColor: theme.colors.backgroundPrimary,
    borderRightColor: theme.colors.borderDefault,
    borderRightWidth: '1px',
    position: 'fixed',
    top: videoSessionOpen ? `${videoSessionBannerHeight}px` : 0,
    bottom: 0,
    zIndex: 1,
  }),
)

// Container for the logo at the top
const LogoContainer = styled(Pressable)<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  height: theme.spacing['40px'],
  padding: `${theme.spacing['4px']} ${theme.spacing['8px']}`,
  flexDirection: 'column',
  alignItems: 'center',
  alignSelf: 'stretch',
  borderRadius: theme.spacing['4px'],
}))

// Container for the navigation icons in the middle
const NavigationContainer = styled.View(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  gap: theme.spacing['8px'],
}))

// Container for the account icon at the bottom
const AccountContainer = styled.View(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing['16px'],
}))

const SideNavIconButton = styled(IconButton)<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  width: '70px',
  height: '70px',
  maxWidth: '70px',
  maxHeight: '70px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: theme.spacing['4px'],
}))

const SideNavBottomIconButton = styled(IconButton)<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  width: '40px',
  height: '40px',
  padding: theme.spacing['8px'],
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const SideNavIconStylingContainer = styled.View({
  display: 'flex',
  width: '70px',
  height: '70px',
  maxWidth: '70px',
  maxHeight: '70px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

const DropdownMenuContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  display: 'flex',
  left: '100%',
  top: '0%',
  marginLeft: spacing['20px'],
  position: 'absolute',
  backgroundColor: colors.backgroundPrimary,
  borderRadius: spacing['8px'],
  boxShadow: `0 4px 20px ${colors.shadowLow}`,
  minWidth: '225px',
  zIndex: 1000,
  border: `1px solid ${colors.borderDefault}`, // Adding a border for better visibility
}))

/**
 * SideNavigation bar Component that is displayed on the left side of the page
 */
export const SideNavigationBar: FunctionComponent<SideNavigationBarProps> = ({
  handleLogoClick,
  navigationItems,
  title,
  menuItemsList,
  onSupportPress,
  hasInAppCalendarSettings,
  onSearchPress,
  onSettingPress,
  videoSessionOpen,
  appLocked,
  videoSessionBannerHeight,
  hideSearchNav,
  hideSettingsNav,
  showSettingsNotificationBubble,
  onNavigate,
  isSettingsActive,
}) => {
  const { colors, spacing } = useTheme()
  // Add a map of dropdown states using indices as keys
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null)
  const [selectedProviderMenuItem, setSelectedProviderMenuItem] = useState<string | null>(null)
  const dropdownRef = useRef(null)

  useOnClickOutside(
    'SideNavDropdownContainer',
    dropdownRef,
    () => {
      setOpenDropdownIndex(null)
    },
    'SideNavTriggerButton',
  )

  const accountBadge = (
    <DefaultAvatar
      displayName={title || ''}
      size={40}
      backgroundColor={appLocked ? colors.iconInactive : colors.primaryButtonBackgroundDefault}
      fontColor={colors.textButton}
      borderRadius={20}
    />
  )

  const squareFocusStyle = {
    borderRadius: spacing['4px'], // Use square border radius instead of default 100px
  }

  return (
    <Container
      testID={tID('SideNavigationBar')}
      videoSessionOpen={videoSessionOpen}
      videoSessionBannerHeight={videoSessionBannerHeight}
    >
      {/* Navigation icons in the middle */}
      <NavigationContainer testID={tID('SideNavigationBar-navContainer')}>
        {/* Logo at the top */}
        <LogoContainer testID={tID('ApplicationHeader-logo')} onPress={handleLogoClick}>
          <LyraLogo width={54.767} height={31} fillColor={colors.icons.lyraLogoPrimaryIcon.fill} />
        </LogoContainer>
        {/* Determine active state for navigation items and their subTabs*/}
        {navigationItems?.map((item, index) => {
          const hasSubTabs = Boolean(item.subTabs?.length)
          // Find active subTab by matching its current path with its active path
          const activeSubTab = hasSubTabs
            ? item.subTabs?.find((subTab) =>
                subTab.currentPathname ? subTab.activePathname.includes(subTab.currentPathname) : false,
              )
            : null

          // Item is active if any subTab is active, or if current path matches item's active path
          const isActive = activeSubTab
            ? true
            : item.currentPathname
              ? item.currentPathname.includes(item.activePathname) &&
                !(
                  item.activePathnameExceptions &&
                  item.activePathnameExceptions.find((activePathnameException) =>
                    item.currentPathname.includes(activePathnameException),
                  )
                )
              : false
          const isDisabled = item.disabled

          const iconColor = isActive ? colors.iconActive : isDisabled ? colors.iconInactive : colors.iconDefault

          if (hasSubTabs && item.subTabs) {
            return (
              <SideNavIconStylingContainer key={`nav-item-${index}`}>
                <SideNavIconButton
                  key={index}
                  onPress={() => setOpenDropdownIndex(openDropdownIndex === index ? null : index)}
                  iconColor={iconColor}
                  leftIconFilled={isActive}
                  customTextColor={iconColor}
                  leftIcon={item._icon}
                  text={item.text}
                  customTextSize={BodyTextSize.CAPTION}
                  testID={tID(item.testID)}
                  size={ButtonSize.SMALL}
                  flexDirection={'column'}
                  id='SideNavTriggerButton'
                  disabled={item.disabled}
                  textStyle={{ marginTop: spacing['4px'] }}
                  focusBorderStyle={squareFocusStyle}
                />
                {openDropdownIndex === index && (
                  <DropdownMenuContainer
                    testID={tID(`${item.testID}-dropdownContainer`)}
                    ref={dropdownRef}
                    id='SideNavDropdownContainer'
                  >
                    <DropdownMenu
                      options={item.subTabs.map((subTab) => subTab.text)}
                      onOptionPress={(selected) => {
                        const selectedSubTab = item.subTabs?.find((option) => option.text === selected)
                        selectedSubTab?.onPress
                          ? selectedSubTab.onPress()
                          : onNavigate(selectedSubTab?.activePathname || '')
                        setOpenDropdownIndex(null)
                      }}
                      value={[activeSubTab?.text || '']}
                      selectedItemStyle={MENU_SELECTED_ITEM_STYLE.LEFT_BORDER}
                      customOptions={item.subTabs.map((subTab) => ({
                        ...subTab,
                        isActive: subTab.activePathname === item.currentPathname,
                      }))}
                    />
                  </DropdownMenuContainer>
                )}
              </SideNavIconStylingContainer>
            )
          }

          // Regular navigation items without subTabs
          return (
            <SideNavIconButton
              key={index}
              onPress={() => (item.onPress ? item.onPress() : onNavigate(item.activePathname))}
              iconColor={iconColor}
              leftIconFilled={isActive}
              customTextColor={iconColor}
              leftIcon={item._icon}
              text={item.text}
              customTextSize={BodyTextSize.CAPTION}
              testID={tID(item.testID)}
              size={ButtonSize.SMALL}
              flexDirection={'column'}
              disabled={item.disabled}
              textStyle={{ marginTop: spacing['4px'] }}
              focusBorderStyle={squareFocusStyle}
            />
          )
        })}
      </NavigationContainer>
      {/* Account section at the bottom */}
      <AccountContainer testID={tID('SideNavigationBar-accountContainer')}>
        {!hideSearchNav && (
          <Tooltip
            wrapperStyle={{ display: 'flex' }}
            content='Search'
            hoverEnabled={!appLocked}
            placement={'right'}
            onPress={appLocked ? undefined : () => onSearchPress()}
            style={{ borderRadius: spacing['4px'] }}
          >
            <SideNavBottomIconButton
              onPress={() => onSearchPress()}
              iconColor={appLocked ? colors.iconInactive : colors.iconDefault}
              leftIcon={<SideNavSearchIcon isFilled={true} />}
              size={ButtonSize.DEFAULT}
              testID={tID('Search-button')}
              disabled={appLocked}
              focusBorderStyle={squareFocusStyle}
              pointerEvents='none'
            />
          </Tooltip>
        )}
        {hasInAppCalendarSettings && !hideSettingsNav && (
          <Tooltip
            content='Settings'
            hoverEnabled={!appLocked}
            placement={'right'}
            onPress={appLocked ? undefined : onSettingPress}
            testID={tID('Settings-button')}
          >
            <SideNavBottomIconButton
              onPress={onSettingPress}
              iconColor={appLocked ? colors.iconInactive : isSettingsActive ? colors.iconActive : colors.iconDefault}
              leftIcon={<SideNavSettingsIcon isFilled={isSettingsActive} />}
              size={ButtonSize.DEFAULT}
              testID={tID('Settings-button')}
              disabled={appLocked}
              focusBorderStyle={squareFocusStyle}
              pointerEvents='none'
              showNotificationBubble={showSettingsNotificationBubble}
            />
          </Tooltip>
        )}
        <Tooltip
          content='Get support'
          hoverEnabled={!appLocked}
          placement={'right'}
          onPress={appLocked ? undefined : () => onSupportPress?.()}
        >
          <SideNavBottomIconButton
            onPress={() => onSupportPress?.()}
            iconColor={appLocked ? colors.iconInactive : colors.iconDefault}
            leftIcon={<SideNavHelpIcon />}
            size={ButtonSize.DEFAULT}
            testID={tID('HelpCenter-button')}
            disabled={appLocked}
            focusBorderStyle={squareFocusStyle}
            pointerEvents='none'
          />
        </Tooltip>
        <Menu
          onPressHandler={(selected) => setSelectedProviderMenuItem(selected)}
          menuItems={menuItemsList}
          customMenuIcon={accountBadge}
          testID={'SideNav-menu'}
          preselectedValue={selectedProviderMenuItem ?? undefined}
          horizontalPosition='left'
          disableBottomSheetForWeb
          dropdownStyle={{
            display: 'flex',
            position: 'absolute',
            left: '100%',
            bottom: '0%',
            top: 'auto',
            marginLeft: spacing['32px'],
          }}
          disabled={appLocked}
        />
      </AccountContainer>
    </Container>
  )
}
